import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { subscribeToMessages } from '@coparent/shared/src/services/messageService';
import { subscribeToCustodyOverrides } from '@coparent/shared/src/services/custodyService';
import { subscribeToExpenses } from '@coparent/shared/src/services/expenseService';
import { subscribeToActivities } from '@coparent/shared/src/services/activityService';
import { subscribeToRules } from '@coparent/shared/src/services/ruleService';
import { subscribeToNotificationState, updateLastSeenAt, dismissNotification } from '@coparent/shared/src/services/notificationService';
import { expandActivityOccurrences } from '@coparent/shared/src/utils/activityUtils';
import { format, startOfDay, addDays } from 'date-fns';
import toast from 'react-hot-toast';

const toMillis = (timestamp) => {
  if (!timestamp) return 0;
  if (timestamp.toMillis) return timestamp.toMillis();
  if (timestamp.seconds) return timestamp.seconds * 1000;
  return 0;
};

const useNotifications = () => {
  const { currentUser } = useAuth();
  const { familyId } = useFamily();

  const [messages, setMessages] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [activities, setActivities] = useState([]);
  const [rules, setRules] = useState([]);
  const [lastSeenAt, setLastSeenAt] = useState(null);
  const [dismissedIds, setDismissedIds] = useState(new Set());

  // Track current date so activity notifications refresh when the day changes
  const [currentDateStr, setCurrentDateStr] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    const interval = setInterval(() => {
      const now = format(new Date(), 'yyyy-MM-dd');
      if (now !== currentDateStr) setCurrentDateStr(now);
    }, 60000); // check every minute
    return () => clearInterval(interval);
  }, [currentDateStr]);

  const hasMountedRef = useRef(false);
  const prevCountsRef = useRef({ messages: 0, swaps: 0 });

  // Subscribe to all data sources
  useEffect(() => {
    if (!familyId || !currentUser) return;

    const unsubs = [
      subscribeToMessages(familyId, setMessages),
      subscribeToCustodyOverrides(familyId, setOverrides),
      subscribeToExpenses(familyId, setExpenses),
      subscribeToActivities(familyId, setActivities),
      subscribeToRules(familyId, setRules),
      subscribeToNotificationState(currentUser.uid, ({ lastSeenAt: ls, dismissedIds: ids }) => {
        setLastSeenAt(ls);
        setDismissedIds(new Set(ids));
      }),
    ];

    return () => unsubs.forEach(u => u());
  }, [familyId, currentUser]);

  // Derive notifications from raw data
  const notifications = useMemo(() => {
    if (!currentUser) return [];

    const uid = currentUser.uid;
    const lastSeenMs = toMillis(lastSeenAt);
    const items = [];

    // Unread messages
    messages.forEach(msg => {
      if (msg.senderId !== uid && msg.read && !msg.read[uid]) {
        const id = `msg-${msg.id}`;
        items.push({
          id,
          type: 'message',
          title: 'New message',
          body: msg.text?.length > 60 ? msg.text.slice(0, 60) + '...' : msg.text || '',
          timestamp: msg.createdAt,
          timestampMs: toMillis(msg.createdAt),
          navigateTo: '/messages',
          isUnread: !dismissedIds.has(id),
        });
      }
    });

    // Pending swap requests awaiting current user's approval
    overrides.forEach(override => {
      if (override.status === 'proposed' && override.proposedBy !== uid) {
        const id = `swap-${override.id}`;
        items.push({
          id,
          type: 'swap',
          title: 'Swap request',
          body: override.reason
            ? `${override.reason} — ${override.date}`
            : `Day swap proposed for ${override.date}`,
          timestamp: override.createdAt,
          timestampMs: toMillis(override.createdAt),
          navigateTo: '/swaps',
          isUnread: !dismissedIds.has(id),
        });
      }
    });

    // Swaps you proposed that co-parent accepted/declined
    overrides.forEach(override => {
      if (
        override.proposedBy === uid &&
        override.status !== 'proposed' &&
        toMillis(override.createdAt) > lastSeenMs
      ) {
        const accepted = override.status === 'confirmed' || override.status === 'accepted';
        const id = `swap-response-${override.id}`;
        items.push({
          id,
          type: 'swap',
          title: accepted ? 'Swap accepted' : 'Swap declined',
          body: `Your swap request for ${override.date} was ${accepted ? 'accepted' : 'declined'}`,
          timestamp: override.createdAt,
          timestampMs: toMillis(override.createdAt),
          navigateTo: '/swaps',
          isUnread: !dismissedIds.has(id),
        });
      }
    });

    // New unsettled expenses from co-parent
    expenses.forEach(expense => {
      const createdMs = toMillis(expense.createdAt);
      if (
        expense.paidBy !== uid &&
        expense.status !== 'settled' &&
        createdMs > lastSeenMs
      ) {
        const id = `exp-${expense.id}`;
        items.push({
          id,
          type: 'expense',
          title: 'New expense',
          body: expense.description
            ? `$${expense.amount} — ${expense.description}`
            : `$${expense.amount}`,
          timestamp: expense.createdAt,
          timestampMs: createdMs,
          navigateTo: '/expenses',
          isUnread: !dismissedIds.has(id),
        });
      }
    });

    // Expenses you created that co-parent settled
    expenses.forEach(expense => {
      const settledMs = toMillis(expense.settledAt);
      if (
        expense.paidBy === uid &&
        expense.status === 'settled' &&
        settledMs > lastSeenMs
      ) {
        const id = `exp-settled-${expense.id}`;
        items.push({
          id,
          type: 'expense',
          title: 'Expense settled',
          body: expense.description
            ? `$${expense.amount} — ${expense.description}`
            : `$${expense.amount}`,
          timestamp: expense.settledAt,
          timestampMs: settledMs,
          navigateTo: '/expenses',
          isUnread: !dismissedIds.has(id),
        });
      }
    });

    // Pending rule proposals awaiting current user
    rules.forEach(rule => {
      if (rule.status === 'proposed' && rule.proposedBy !== uid) {
        const id = `rule-${rule.id}`;
        items.push({
          id,
          type: 'rule',
          title: 'Rule proposed',
          body: rule.title,
          timestamp: rule.createdAt,
          timestampMs: toMillis(rule.createdAt),
          navigateTo: '/rules',
          isUnread: !dismissedIds.has(id),
        });
      }
    });

    // Rules you proposed that co-parent responded to
    rules.forEach(rule => {
      if (
        rule.proposedBy === uid &&
        rule.status !== 'proposed' &&
        toMillis(rule.respondedAt) > lastSeenMs
      ) {
        const agreed = rule.status === 'agreed';
        const id = `rule-response-${rule.id}`;
        items.push({
          id,
          type: 'rule',
          title: agreed ? 'Rule agreed' : 'Rule needs discussion',
          body: rule.title,
          timestamp: rule.respondedAt,
          timestampMs: toMillis(rule.respondedAt),
          navigateTo: '/rules',
          isUnread: !dismissedIds.has(id),
        });
      }
    });

    // Drop-off / pick-up / removal changes made by co-parent
    const formatShort = (dateStr) => {
      if (!dateStr) return '';
      const [y, m, d] = dateStr.split('-').map(Number);
      if (!y || !m || !d) return dateStr;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[m - 1]} ${d}`;
    };
    activities.forEach(activity => {
      const modifiedBy = activity.lastModifiedBy;
      const modifiedAtMs = toMillis(activity.lastModifiedAt);
      const kind = activity.lastModifiedKind;
      const dateStr = activity.lastModifiedDate;
      if (!modifiedBy || modifiedBy === uid) return;
      if (!modifiedAtMs || modifiedAtMs <= lastSeenMs) return;
      if (!kind || !dateStr) return;

      const id = `actmod-${activity.id}-${dateStr}-${modifiedAtMs}`;
      const title = kind === 'excluded' ? 'Activity removed' : `${activity.name} · ${formatShort(dateStr)}`;
      const body = kind === 'excluded'
        ? `${activity.name} on ${formatShort(dateStr)} was removed`
        : 'Drop-off or pick-up changed';
      items.push({
        id,
        type: 'activity',
        title,
        body,
        timestamp: activity.lastModifiedAt,
        timestampMs: modifiedAtMs,
        navigateTo: '/calendar',
        isUnread: !dismissedIds.has(id),
      });
    });

    // Upcoming activities (today + tomorrow)
    const today = startOfDay(new Date());
    const endOfTomorrow = addDays(today, 2);
    activities.forEach(activity => {
      const occurrences = expandActivityOccurrences(activity, today, endOfTomorrow);
      occurrences.forEach(occ => {
        const isToday = occ.date === format(today, 'yyyy-MM-dd');
        items.push({
          id: `act-${activity.id}-${occ.date}`,
          type: 'activity',
          title: isToday ? 'Today' : 'Tomorrow',
          body: `${activity.name}${activity.location ? ` at ${activity.location}` : ''}`,
          timestamp: null,
          timestampMs: new Date(occ.date + 'T00:00:00').getTime(),
          navigateTo: '/activities',
          isUnread: false,
        });
      });
    });

    // Sort by timestamp descending (newest first), activities by date
    items.sort((a, b) => b.timestampMs - a.timestampMs);

    return items;
  }, [currentUser, messages, overrides, expenses, activities, rules, lastSeenAt, dismissedIds, currentDateStr]);

  // Unread count (activities don't count)
  const unreadCount = useMemo(() => {
    return notifications.filter(n => n.isUnread).length;
  }, [notifications]);

  // Toast for new arrivals (skip initial mount)
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      const msgCount = notifications.filter(n => n.type === 'message').length;
      const swapCount = notifications.filter(n => n.type === 'swap').length;
      const ruleCount = notifications.filter(n => n.type === 'rule').length;
      prevCountsRef.current = { messages: msgCount, swaps: swapCount, rules: ruleCount };
      return;
    }

    const msgCount = notifications.filter(n => n.type === 'message').length;
    const swapCount = notifications.filter(n => n.type === 'swap').length;
    const ruleCount = notifications.filter(n => n.type === 'rule').length;

    if (msgCount > prevCountsRef.current.messages) {
      toast('New message received', { icon: '💬' });
    }
    if (swapCount > prevCountsRef.current.swaps) {
      toast('New swap request', { icon: '🔄' });
    }
    if (ruleCount > prevCountsRef.current.rules) {
      toast('New rule proposal', { icon: '📋' });
    }

    prevCountsRef.current = { messages: msgCount, swaps: swapCount, rules: ruleCount };
  }, [notifications]);

  const markAllRead = useCallback(async () => {
    if (currentUser) {
      await updateLastSeenAt(currentUser.uid);
    }
  }, [currentUser]);

  const markOneRead = useCallback(async (notificationId) => {
    if (currentUser) {
      await dismissNotification(currentUser.uid, notificationId);
    }
  }, [currentUser]);

  return { notifications, unreadCount, markAllRead, markOneRead, lastSeenAt };
};

export default useNotifications;
